import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import {
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Container,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

import {
  TUpdateCourseAsAdmin,
  getCourseByIdForAdmin,
  updateCourseAsAdmin,
  deleteCourseAsAdmin,
  getCoursesState,
  resetAdminCourse,
  resetRedirectUrl,
} from '../../../../store/V2/CoursesSlice';
import {
  ISection,
  TCreateCourseSectionAsAdmin,
  TSectionTypes,
  createNewCourseSectionAsAdmin,
  getAdminCourseSectionsByCourseId,
  getSectionsState,
  resetAdminCourseSections,
  resetNewlyAddedSectionId,
  updateAdminCourseSectionState,
} from '../../../../store/V2/SectionSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { updateCourseAsAdminSavingSlice } from '../../../../store/V2/SavingSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import { getCourseTemplatesState } from '../../../../store/V2/CourseTemplatesSlice';
import { TP_COLORS } from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { checkUserPermission } from '../../../../helper';
import ClassListOptionsModal, {
  TClassListOptionsModalOption,
} from './modals/ClassListOptionsModal';
import CourseOptionsModal from './modals/CourseOptionsModal';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import EditableTextWithTextField from '../../../../components/Inputs/EditableTextWithTextField';
import SectionItem, {
  TSubsectionCreateNewItemRequestData,
} from './components/SectionItem';
import NewSectionButton from './components/NewSectionButton';
import SavingStateViewer from '../../../../components/Typography/SavingStateViewer';
import EditableTextWithDraftEditor from '../../../../components/Inputs/EditableTextWithDraftEditor';
import ClassDetailViewer from './components/ClassDetailViewer';
import AddZoomSectionItemModal from './modals/ClassListOptionsModal/AddZoomSectionItemModal';
import SectionOptionsModal from './modals/SectionOptionsModal';
import AddResourceSectionItemModal from './modals/ClassListOptionsModal/AddResourceSectionItemModal';
import AddCourseTemplateModal from './modals/AddCourseTemplateModal';
import ViewTemplateModal from './modals/ViewTemplateModal';
import ClassCancellationModal from './modals/ClassCancellationModal';
import ClassRescheduleModal from './modals/ClassRescheduleModal';
import AddHomeworkSectionItemModal from './modals/ClassListOptionsModal/AddHomeworkSectionItemModal';
import AdminChat from '../../Chat/admin';
import {
  getAdminChatState,
  resetAdminCourseChatData,
} from '../../../../store/V2/AdminChatSlice';
import {
  getCourseTutorsAsAdmin,
  resetAdminCourseTutorsData,
} from '../../../../store/V2/CourseTutorsSlice';
import {
  getCourseStudentsAsAdmin,
  resetAdminCourseStudentsData,
} from '../../../../store/V2/CourseStudentsSlice';
import UpdateClassModal from './modals/UpdateClassModal';

export type TClassListOptionsModalData = {
  open: boolean;
  data?: TSubsectionCreateNewItemRequestData;
};

type TCourseState = {
  title: string;
  description: string;
};

const initialCourseState: TCourseState = {
  title: '',
  description: '', // HTML
};

const ViewAdminCoursePage: React.FC = () => {
  const theme = useTheme();
  const urlParams = useParams();
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseTemplatesState = useAppSelector(getCourseTemplatesState);
  const sectionsState = useAppSelector(getSectionsState);
  const authState = useAppSelector(getAuthState);
  const adminChatState = useAppSelector(getAdminChatState);

  // State
  const [initialLoading, setInitialLoading] = useState(true);
  const [courseState, setCourseState] = useState(initialCourseState);
  const [isClassListCollapseOpen, setClassListCollapseOpen] = useState(true);
  const [openedSections, setOpenedSections] = useState<Array<string>>([]);
  const [isCourseOptionsModaOpen, setCourseOptionsModalOpen] = useState(false);
  const [isClassDetailsCollapseOpen, setClassDetailsCollapseOpen] = useState(
    true
  );

  const [
    selectedParentSectionItemData,
    setSelectedParentSectionItemData,
  ] = useState<
    TSubsectionCreateNewItemRequestData['parentSection'] | undefined
  >(undefined);

  const [isClassListOptionsModalOpen, setClassListOptionsModalOpen] = useState(
    false
  );

  const [
    addZoomSectionItemModalData,
    setAddZoomSectionItemModalData,
  ] = useState<{
    open: boolean;
    platform?: 'ZOOM' | 'TEAMS';
  }>({ open: false });

  const [
    editCourseSectionZoomItemModalData,
    setEditCourseSectionZoomItemModalData,
  ] = useState<{ open: boolean; section?: ISection }>({
    open: false,
  });

  const [sectionOptionsModalData, setSectionOptionsModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [isAddResourceItemModalOpen, setAddResourceItemModalOpen] = useState(
    false
  );

  const [
    editCourseSectionResourceItemModalData,
    setEditCourseSectionResourceItemModalData,
  ] = useState<{ open: boolean; section?: ISection }>({
    open: false,
  });

  const [isAddHomeworkItemModalOpen, setAddHomeworkItemModalOpen] = useState(
    false
  );

  const [
    editHomeworkSectionItemModalData,
    setEditHomeworkSectionItemModalData,
  ] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [
    isCourseTemplateAddingModalOpen,
    setCourseTemplateAddingModalOpen,
  ] = useState(false);

  const [isViewTemplateModelOpen, setViewTemplateModelOpen] = useState(false);

  const [classCancellationModalData, setClassCancellationModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [classRescheduleModalData, setClassRescheduleModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const [classUpdateModalData, setClassUpdateModalData] = useState<{
    open: boolean;
    section?: ISection;
  }>({
    open: false,
  });

  const courseId = urlParams.id;
  const courseData = coursesState.adminCourse;
  const courseSections = sectionsState.adminCourseSections || [];
  const isAllSectionsOpened = courseSections.every((item) =>
    openedSections.some((id) => id === item._id)
  );

  const isChatEnabled = Boolean(
    courseData &&
      courseData.chat &&
      courseData.chat.isChatEnabled &&
      adminChatState.isConnected
  );

  let numberOfClassesAndDurationText = '0 Class';

  if (courseData && courseData.noOfClasses) {
    numberOfClassesAndDurationText = `${courseData.noOfClasses} Class`;

    if (courseData.noOfClasses > 1) {
      numberOfClassesAndDurationText += 'es';
    }

    if (courseData.duration) {
      const _totalDuration = courseData.noOfClasses * courseData.duration;
      const _hours = Math.floor(_totalDuration / 60);
      const _mins = _totalDuration % 60;

      let _formatedDuration = '';

      if (_hours) {
        _formatedDuration = `${_hours} hr`;

        if (_mins) {
          _formatedDuration += ` ${_mins} min`;
        }
      } else {
        _formatedDuration += ` ${_mins} min`;
      }

      numberOfClassesAndDurationText += ` | ${_formatedDuration}`;
    }
  }

  useEffect(() => {
    initialize();

    return () => {
      dispatch(resetAdminCourse());
      dispatch(resetAdminCourseSections());
      dispatch(resetRedirectUrl());
      dispatch(resetAdminCourseChatData());
      dispatch(resetAdminCourseTutorsData());
      dispatch(resetAdminCourseStudentsData());
    };
  }, [courseId]);

  useEffect(() => {
    if (courseData && !isViewTemplateModelOpen) {
      setCourseState({
        title: courseData.title,
        description: courseData.description,
      });
    }
  }, [courseData]);

  useEffect(() => {
    setOpenedSections((prevState) => {
      const _newList = [...prevState];

      if (sectionsState.newlyAddedSectionId) {
        _newList.push(sectionsState.newlyAddedSectionId);
      }

      dispatch(resetNewlyAddedSectionId());

      return _newList;
    });
  }, [sectionsState.newlyAddedSectionId]);

  useEffect(() => {
    if (coursesState.redirectUrl) {
      navigate(coursesState.redirectUrl, { replace: true });
    }
  }, [coursesState.redirectUrl]);

  const initialize = async () => {
    if (courseId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const courseRes: any = await dispatch(
        getCourseByIdForAdmin({ id: courseId })
      );

      await dispatch(getAdminCourseSectionsByCourseId(courseId));
      await dispatch(
        getCourseTutorsAsAdmin({
          course: courseId,
          limit: 200,
          offset: 0,
        })
      );
      await dispatch(
        getCourseStudentsAsAdmin({
          course: courseId,
          limit: 200,
          offset: 0,
        })
      );

      if (courseRes && courseRes.payload && courseRes.payload.template) {
        if (
          !(
            courseRes.payload.templateSteps &&
            courseRes.payload.templateSteps.isCompleted
          )
        ) {
          toggleViewTemplateModal();
        }
      }
    }

    setInitialLoading(false);
  };

  const toggleCourseOptionsModal = () => {
    setCourseOptionsModalOpen(!isCourseOptionsModaOpen);
  };

  const toggleClassDetailsCollapse = () => {
    setClassDetailsCollapseOpen(!isClassDetailsCollapseOpen);
  };

  const toggleViewTemplateModal = () => {
    setViewTemplateModelOpen((prevState) => !prevState);
  };

  const updateCourseState = (attributes: Partial<TCourseState>) => {
    setCourseState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const updateCourseDataByAttributes = (
    attributes: Partial<TUpdateCourseAsAdmin>
  ) => {
    if (courseId) {
      const apiData: TUpdateCourseAsAdmin = {
        _id: courseId,
        ...attributes,
      };

      dispatch(updateCourseAsAdminSavingSlice(apiData));
    }
  };

  const toggleClassListCollapse = () => {
    setClassListCollapseOpen(!isClassListCollapseOpen);
  };

  const handleSectionCollapse = (section: ISection) => {
    let newOpenedSections = [...openedSections];

    const match = newOpenedSections.some((item) => item === section._id);

    if (match) {
      newOpenedSections = newOpenedSections.filter(
        (item) => item !== section._id
      );
    } else {
      newOpenedSections.push(section._id);
    }

    setOpenedSections(newOpenedSections);
  };

  const createNewSection = () => {
    if (courseId) {
      dispatch(
        createNewCourseSectionAsAdmin({
          sortingKey: courseSections.length + 1,
          course: courseId,
          title: 'New Title',
          description: 'New Description',
        })
      );
    }
  };

  const toggleAddCourseTemplateModal = () => {
    setCourseTemplateAddingModalOpen((prevState) => !prevState);
  };

  const openSectionOptionsModalForSectionItem = (
    data: TSubsectionCreateNewItemRequestData
  ) => {
    setClassListOptionsModalOpen(true);
    setSelectedParentSectionItemData(data.parentSection);
  };

  const closeSectionOptionsModalForSectionItem = () => {
    setClassListOptionsModalOpen(false);
    setSelectedParentSectionItemData(undefined);
  };

  const closeAddZoomSectionItemModal = () => {
    setAddZoomSectionItemModalData({ open: false });
    setSelectedParentSectionItemData(undefined);
  };

  const closeAddResourceItemModal = () => {
    setAddResourceItemModalOpen(false);
    setSelectedParentSectionItemData(undefined);
  };

  const closeEditCourseSectionZoomItemModal = () => {
    setEditCourseSectionZoomItemModalData({
      open: false,
    });
  };

  const openSectionOptionsModal = (section: ISection) => {
    setSectionOptionsModalData({
      open: true,
      section,
    });
  };

  const closeSectionOptionsModal = () => {
    setSectionOptionsModalData({
      open: false,
    });
  };

  const openEditResourceItemModal = (section: ISection) => {
    setEditCourseSectionResourceItemModalData({
      section,
      open: true,
    });
  };

  const closeEditResourceItemModal = () => {
    setEditCourseSectionResourceItemModalData({
      open: false,
    });
  };

  const closeAddHomeworkItemModal = () => {
    setAddHomeworkItemModalOpen(false);
    setSelectedParentSectionItemData(undefined);
  };

  const openEditHomeworkItemModal = (section: ISection) => {
    setEditHomeworkSectionItemModalData({
      section,
      open: true,
    });
  };

  const closeEditHomeworkItemModal = () => {
    setEditHomeworkSectionItemModalData({
      open: false,
    });
  };

  const onClassListOptionsModalSubmit = (
    option: TClassListOptionsModalOption
  ) => {
    if (option.key === 'newSection') {
      if (courseId && selectedParentSectionItemData) {
        const apiData: TCreateCourseSectionAsAdmin = {
          parent: selectedParentSectionItemData._id,
          sortingKey: selectedParentSectionItemData.nextSortingKey,
          course: courseId,
          title: 'New Title',
          description: 'New Description',
        };

        dispatch(createNewCourseSectionAsAdmin(apiData));
      }

      closeSectionOptionsModalForSectionItem();
    } else if (option.key === 'zoom') {
      setClassListOptionsModalOpen(false);
      setAddZoomSectionItemModalData({ open: true, platform: 'ZOOM' });
    } else if (option.key === 'teams') {
      setClassListOptionsModalOpen(false);
      setAddZoomSectionItemModalData({ open: true, platform: 'TEAMS' });
    } else if (option.key === 'resource') {
      setClassListOptionsModalOpen(false);
      setAddResourceItemModalOpen(true);
    } else if (option.key === 'homework') {
      setClassListOptionsModalOpen(false);
      setAddHomeworkItemModalOpen(true);
    }
  };

  const handleDeleteCourse = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this course?',
    }).then(() => {
      if (courseId) {
        dispatch(deleteCourseAsAdmin(courseId));
      }
    });
  };

  const handlePublishCourse = () => {
    if (courseData && courseId) {
      confirm({
        title: 'Are you sure?',
        description:
          courseData.status === 'DRAFT'
            ? 'Do you want to publish this course?'
            : 'Do you want to unpublish this course?',
      }).then(() => {
        const apiData: TUpdateCourseAsAdmin = {
          _id: courseId,
          status: courseData.status === 'DRAFT' ? 'ACTIVE' : 'DRAFT',
        };

        dispatch(updateCourseAsAdmin(apiData));
      });
    }
  };

  const handleCollapseAllSections = () => {
    setOpenedSections((prevState) => {
      if (isAllSectionsOpened) {
        return [];
      }

      const newList = [...prevState];

      courseSections.forEach((item) => {
        if (!newList.some((id) => id === item._id)) {
          newList.push(item._id);
        }
      });

      return newList;
    });
  };

  const openClassCancellationModal = (section: ISection) => {
    setClassCancellationModalData({
      section,
      open: true,
    });
  };

  const closeClassCancellationModal = () => {
    setClassCancellationModalData({
      open: false,
    });
  };

  const openClassRescheduleModal = (section: ISection) => {
    setClassRescheduleModalData({
      section,
      open: true,
    });
  };

  const closeClassRescheduleModal = () => {
    setClassRescheduleModalData({
      open: false,
    });
  };

  const openClassUpdateModal = (section: ISection) => {
    setClassUpdateModalData({
      section,
      open: true,
    });
  };

  const closeClassUpdateModal = () => {
    setClassUpdateModalData({
      open: false,
    });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'link',
        title: 'Courses',
        link: `${process.env.PUBLIC_URL}/portal/courses`,
      },
    ];

    if (courseData) {
      options.push({
        type: 'link',
        title: courseData.title,
        highlight: true,
      });
    }

    return options;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <>
      <Container maxWidth="lg">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        {coursesState.status === 'loading' && !courseData ? (
          <BasicLoader />
        ) : (
          <>
            {courseData ? (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                mb="15px"
              >
                <SavingStateViewer />
                {courseData.isOneToOne ? (
                  <Chip
                    size="small"
                    label="One to One Course"
                    sx={{
                      borderRadius: '4px',
                      fontWeight: 600,
                      backgroundColor: '#1E293B',
                      color: '#fff',
                    }}
                  />
                ) : null}
                {checkUserPermission(authState.user, {
                  adminCourse: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_DELETE,
                  ],
                }) && (
                  <Tooltip title="Delete Course" placement="top">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={
                        initialLoading || coursesState.status === 'loading'
                      }
                      onClick={handleDeleteCourse}
                    >
                      Delete Course
                    </Button>
                  </Tooltip>
                )}
                {checkUserPermission(authState.user, {
                  adminCourse: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_PUBLISH,
                  ],
                }) &&
                (courseData.groupClassId || courseData.isOneToOne) ? (
                  <Tooltip
                    placement="top"
                    title={
                      courseData.status === 'DRAFT'
                        ? 'Publish Course'
                        : 'Unpublish Course'
                    }
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={
                        initialLoading || coursesState.status === 'loading'
                      }
                      onClick={handlePublishCourse}
                    >
                      {courseData.status === 'DRAFT'
                        ? 'Publish Course'
                        : 'Unpublish Course'}
                    </Button>
                  </Tooltip>
                ) : null}
                {checkUserPermission(authState.user, {
                  adminCourseTemplate: [
                    USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_CREATE,
                  ],
                }) ? (
                  <Tooltip placement="top" title="Save as Template">
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={
                        initialLoading ||
                        courseTemplatesState.status === 'loading'
                      }
                      onClick={toggleAddCourseTemplateModal}
                    >
                      Save as Template
                    </Button>
                  </Tooltip>
                ) : null}
                <Tooltip title="Settings" placement="top">
                  <ButtonBase
                    disabled={initialLoading}
                    onClick={toggleCourseOptionsModal}
                    sx={{
                      backgroundColor: '#F1F5F9',
                      padding: '5px',
                      borderRadius: '5px',
                    }}
                  >
                    <SettingsIcon
                      sx={{
                        color: initialLoading
                          ? 'rgba(0, 0, 0, 0.26)'
                          : undefined,
                      }}
                    />
                  </ButtonBase>
                </Tooltip>
              </Stack>
            ) : null}
          </>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md>
            {courseData ? (
              <>
                <Stack direction="row" alignItems="center" flexGrow={1}>
                  <IconButton
                    style={{ marginRight: '5px' }}
                    onClick={toggleClassDetailsCollapse}
                  >
                    {isClassDetailsCollapseOpen ? (
                      <ExpandLessIcon style={{ color: TP_COLORS.black }} />
                    ) : (
                      <ExpandMoreIcon style={{ color: TP_COLORS.black }} />
                    )}
                  </IconButton>
                  <EditableTextWithTextField
                    editable={checkUserPermission(authState.user, {
                      adminCourse: [
                        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                      ],
                    })}
                    label={courseState.title}
                    typographyProps={{
                      fontSize: '25px',
                      fontWeight: 700,
                      lineHeight: '30px',
                    }}
                    onSubmit={(newTitle) => {
                      updateCourseDataByAttributes({
                        title: newTitle,
                      });

                      updateCourseState({
                        title: newTitle,
                      });
                    }}
                  />
                </Stack>
                <Collapse in={isClassDetailsCollapseOpen}>
                  <div
                    style={{ padding: `0px ${isXsScreen ? '15px' : '50px'}` }}
                  >
                    <div style={{ marginBottom: '30px' }}>
                      <EditableTextWithDraftEditor
                        editable={checkUserPermission(authState.user, {
                          adminCourse: [
                            USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                          ],
                        })}
                        label={courseState.description}
                        onSubmit={(newDescription) => {
                          updateCourseDataByAttributes({
                            description: newDescription,
                          });

                          updateCourseState({
                            description: newDescription,
                          });
                        }}
                        textContainerSx={{
                          '& p': {
                            margin: 0,
                          },
                        }}
                        containerStyles={{
                          color: '#647488',
                          pt: '27px',
                        }}
                      />
                    </div>
                    <div>
                      <ClassDetailViewer course={courseData} />
                    </div>
                  </div>
                </Collapse>
                <div
                  style={{
                    height: '1px',
                    borderBottom: '1px dotted #94A3B8',
                    marginTop: '40px',
                  }}
                />
                {courseData.groupClassId || courseData.isOneToOne ? (
                  <>
                    <Stack
                      mt="40px"
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb="24px"
                    >
                      <Stack>
                        <Stack direction="row" alignItems="center">
                          <IconButton
                            style={{ marginRight: '5px' }}
                            onClick={toggleClassListCollapse}
                          >
                            {isClassListCollapseOpen ? (
                              <ExpandLessIcon
                                style={{ color: TP_COLORS.black }}
                              />
                            ) : (
                              <ExpandMoreIcon
                                style={{ color: TP_COLORS.black }}
                              />
                            )}
                          </IconButton>
                          <Typography
                            variant="h5"
                            fontSize="25px"
                            fontWeight={700}
                          >
                            Class Details
                          </Typography>
                        </Stack>
                        <Typography
                          variant="caption"
                          fontSize="14px"
                          fontWeight={500}
                          color="#647488"
                          ml="48px"
                          mt="-6px"
                        >
                          {numberOfClassesAndDurationText}
                        </Typography>
                      </Stack>
                      {courseSections.length ? (
                        <Button
                          variant="text"
                          color="info"
                          size="small"
                          onClick={handleCollapseAllSections}
                        >
                          {isAllSectionsOpened
                            ? 'Collapse all sections'
                            : 'Expand all sections'}
                        </Button>
                      ) : null}
                    </Stack>
                    <Collapse in={isClassListCollapseOpen}>
                      {sectionsState.status === 'loading' &&
                      !courseSections.length ? (
                        <div style={{ marginBottom: '15px' }}>
                          <BasicLoader />
                        </div>
                      ) : null}
                      <Stack spacing="8px">
                        {courseSections.map((item, index) => {
                          const _keyName = `${index}`;
                          const _orderNumber = index + 1;
                          const _collapse = openedSections.some(
                            (itemId) => itemId === item._id
                          );

                          return (
                            <Stack id={_keyName} key={_keyName}>
                              <SectionItem
                                isOneToOne={courseData.isOneToOne || false}
                                openedSections={openedSections}
                                keyName={_keyName}
                                orderNumber={_orderNumber}
                                section={item}
                                collapse={_collapse}
                                toggleCollapse={handleSectionCollapse}
                                onEditResourceItem={openEditResourceItemModal}
                                onEditHomeworkItem={openEditHomeworkItemModal}
                                onCancelClass={openClassCancellationModal}
                                onRescheduleClass={openClassRescheduleModal}
                                onUpdateClass={openClassUpdateModal}
                                onAddNewItem={
                                  openSectionOptionsModalForSectionItem
                                }
                                showSectionoptionsModal={
                                  openSectionOptionsModal
                                }
                              />
                            </Stack>
                          );
                        })}
                        {checkUserPermission(authState.user, {
                          adminCourse: [
                            USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT,
                          ],
                        }) ? (
                          <NewSectionButton
                            disabled={sectionsState.status === 'loading'}
                            onClick={createNewSection}
                          />
                        ) : null}
                      </Stack>
                    </Collapse>
                    <div
                      style={{
                        height: '1px',
                        borderBottom: '1px dotted #94A3B8',
                        marginTop: '30px',
                      }}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </Grid>
          {isChatEnabled ? <AdminChat /> : null}
        </Grid>
      </Container>
      {/* Modals */}
      {isCourseOptionsModaOpen ? (
        <CourseOptionsModal
          open={isCourseOptionsModaOpen}
          onClose={toggleCourseOptionsModal}
        />
      ) : null}
      {isClassListOptionsModalOpen ? (
        <ClassListOptionsModal
          open={isClassListOptionsModalOpen}
          onClose={closeSectionOptionsModalForSectionItem}
          onSubmit={onClassListOptionsModalSubmit}
        />
      ) : null}
      {sectionOptionsModalData &&
      sectionOptionsModalData.open &&
      sectionOptionsModalData.section ? (
        <SectionOptionsModal
          open={sectionOptionsModalData.open}
          onClose={closeSectionOptionsModal}
          section={sectionOptionsModalData.section}
        />
      ) : null}
      {/* ZOOM ITEM ADD & EDIT */}
      {addZoomSectionItemModalData.open &&
      addZoomSectionItemModalData.platform &&
      selectedParentSectionItemData ? (
        <AddZoomSectionItemModal
          platform={addZoomSectionItemModalData.platform}
          open={addZoomSectionItemModalData.open}
          onClose={closeAddZoomSectionItemModal}
          parentSectionItemData={selectedParentSectionItemData}
        />
      ) : null}
      {/* {editCourseSectionZoomItemModalData &&
      editCourseSectionZoomItemModalData.open &&
      addZoomSectionItemModalData.platform &&
      editCourseSectionZoomItemModalData.section ? (
        <AddZoomSectionItemModal
          mode="edit"
          open={editCourseSectionZoomItemModalData.open}
          onClose={closeEditCourseSectionZoomItemModal}
          section={editCourseSectionZoomItemModalData.section}
        />
      ) : null} */}
      {/* RESOURCE ITEM ADD & EDIT */}
      {isAddResourceItemModalOpen && selectedParentSectionItemData ? (
        <AddResourceSectionItemModal
          open={isAddResourceItemModalOpen}
          onClose={closeAddResourceItemModal}
          parentSectionItemData={selectedParentSectionItemData}
        />
      ) : null}
      {editCourseSectionResourceItemModalData &&
      editCourseSectionResourceItemModalData.open &&
      editCourseSectionResourceItemModalData.section ? (
        <AddResourceSectionItemModal
          mode="edit"
          open={editCourseSectionResourceItemModalData.open}
          onClose={closeEditResourceItemModal}
          section={editCourseSectionResourceItemModalData.section}
        />
      ) : null}
      {/* Homework */}
      {isAddHomeworkItemModalOpen && selectedParentSectionItemData ? (
        <AddHomeworkSectionItemModal
          open={isAddHomeworkItemModalOpen}
          onClose={closeAddHomeworkItemModal}
          parentSectionItemData={selectedParentSectionItemData}
        />
      ) : null}
      {editHomeworkSectionItemModalData &&
      editHomeworkSectionItemModalData.open &&
      editHomeworkSectionItemModalData.section ? (
        <AddHomeworkSectionItemModal
          edit
          open={editHomeworkSectionItemModalData.open}
          onClose={closeEditHomeworkItemModal}
          section={editHomeworkSectionItemModalData.section}
        />
      ) : null}
      {/* Course Template */}
      {isCourseTemplateAddingModalOpen ? (
        <AddCourseTemplateModal
          open={isCourseTemplateAddingModalOpen}
          onClose={toggleAddCourseTemplateModal}
        />
      ) : null}
      {isViewTemplateModelOpen ? (
        <ViewTemplateModal
          open={isViewTemplateModelOpen}
          onClose={toggleViewTemplateModal}
        />
      ) : null}
      {/* Class Cancellation */}
      {classCancellationModalData &&
      classCancellationModalData.open &&
      classCancellationModalData.section ? (
        <ClassCancellationModal
          open={classCancellationModalData.open}
          onClose={closeClassCancellationModal}
          sectionId={classCancellationModalData.section._id}
          sectionType={classCancellationModalData.section.type}
          onSubmitted={(section) => {
            dispatch(
              updateAdminCourseSectionState({
                section,
                id: section._id,
              })
            );

            closeClassCancellationModal();
          }}
        />
      ) : null}
      {/* Class Reschedule */}
      {classRescheduleModalData &&
      classRescheduleModalData.open &&
      classRescheduleModalData.section ? (
        <ClassRescheduleModal
          open={classRescheduleModalData.open}
          onClose={closeClassRescheduleModal}
          sectionId={classRescheduleModalData.section._id}
          onSubmitted={(section) => {
            dispatch(
              updateAdminCourseSectionState({
                section,
                id: section._id,
              })
            );

            closeClassRescheduleModal();
          }}
        />
      ) : null}
      {/* Class Update */}
      {classUpdateModalData.open && classUpdateModalData.section ? (
        <UpdateClassModal
          open={classUpdateModalData.open}
          onClose={closeClassUpdateModal}
          sectionId={classUpdateModalData.section._id}
          onSubmitted={(newSection) => {
            dispatch(
              updateAdminCourseSectionState({
                id: newSection._id,
                section: newSection,
              })
            );

            closeClassUpdateModal();
          }}
        />
      ) : null}
    </>
  );
};

export default ViewAdminCoursePage;
